import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = window.location.origin;

// Check if user is admin (skip tracking for admins)
const isAdminUser = () => {
  return localStorage.getItem('soiree_approved') === 'true' || 
         localStorage.getItem('auth_session_token');
};

// Check if this is a bot or automated tool
const isBot = () => {
  const ua = navigator.userAgent.toLowerCase();
  const botPatterns = [
    'bot', 'crawler', 'spider', 'headless', 'phantom', 'selenium',
    'puppeteer', 'playwright', 'webdriver', 'chrome-lighthouse',
    'pagespeed', 'googlebot', 'bingbot', 'slurp', 'duckduckbot',
    'facebookexternalhit', 'twitterbot', 'linkedinbot', 'embedly',
    'quora link preview', 'showyoubot', 'outbrain', 'pinterest',
    'slackbot', 'vkshare', 'w3c_validator', 'whatsapp', 'flipboard',
    'tumblr', 'bitlybot', 'skypeuripreview', 'nuzzel', 'discordbot',
    'qwantify', 'pinterestbot', 'bitrix', 'ahrefsbot', 'semrushbot',
    'dotbot', 'applebot', 'petalbot', 'yandexbot', 'bytespider'
  ];
  
  // Check user agent for bot patterns
  if (botPatterns.some(pattern => ua.includes(pattern))) {
    return true;
  }
  
  // Check for headless browser indicators
  if (navigator.webdriver) {
    return true;
  }
  
  // Check for missing browser features that bots often lack
  if (!window.chrome && ua.includes('chrome')) {
    return true;
  }
  
  return false;
};

// Generate or get visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('dd_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('dd_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (new per browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('dd_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('dd_session_id', sessionId);
  }
  return sessionId;
};

// Page title mapping
const PAGE_TITLES = {
  '/': 'Home',
  '/admin': 'Admin Dashboard',
  '/hthf': 'High Tech High Fashion',
  '/hthf-partner': 'HTHF Partnership',
  '/emergent': 'Emergent Partnership',
  '/womenbusinesssoiree': 'Women Business Soiree Workshop',
  '/womenbusinesssoiree/recap': 'Workshop Recap',
  '/lab/frontrow/phygital': 'Front Row Lab',
  '/lab/frontrow/month-1': 'Front Row Month 1',
  '/recap/womens-soiree': "Women's Soiree Recap",
};

// Track page view
const trackPageView = async (path) => {
  // Skip tracking for admin users
  if (isAdminUser()) {
    console.debug('Analytics: Skipping tracking for admin user');
    return;
  }
  
  // Skip tracking for bots
  if (isBot()) {
    console.debug('Analytics: Skipping tracking for bot/automated tool');
    return;
  }
  
  try {
    await fetch(`${API_URL}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page_path: path,
        page_title: PAGE_TITLES[path] || path,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
      }),
    });
  } catch (error) {
    // Silently fail - don't break the app for analytics
    console.debug('Analytics tracking failed:', error);
  }
};

// Analytics Tracker Component
export const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

export default AnalyticsTracker;
